import type { LevelBarStyleToken } from "@yjsgames/phaser-ui";
import type { SoundCategory } from "@yjsgames/sound";
import type { TargetFpsSetting } from "@yjsgames/perf";
import type { NumberFormat } from "../models/ModuleBrickSaveData.js";
import type { LanguageCode } from "../i18n/locales.js";
import { PALETTE_NUM } from "../themes/palette.js";

export const NUMBER_FORMAT_OPTIONS: readonly NumberFormat[] = ["auto", "kmg", "eastAsian", "alphabet"];

export const SOUND_LABEL_KEY: Record<SoundCategory, string> = {
  bgm: "settings.soundBgm",
  sfx: "settings.soundSfx",
  ui: "settings.soundUi",
};

export const SETTINGS_PANEL_WIDTH = 344;
export const SETTINGS_PANEL_HEIGHT_LIVE = 604;
export const SETTINGS_PANEL_HEIGHT_DEV = 692;
export const SETTINGS_INLINE_BANNER_VERTICAL_OFFSET = 66;
export const SETTINGS_DEV_BTN_STEP = 48;
export const SETTINGS_ACCOUNT_BTN = { topOffset: 464, width: 240, height: 46 } as const;
export const SETTINGS_BANNER_SLOT = { topOffset: 256, width: 320, height: 50 } as const;
export const SETTINGS_LABEL_ICON_SIZE = 16;

export const settingsPanelHeight = (
  cheatAvailable: boolean,
  inlineBannerEnabled = true,
): number =>
  (cheatAvailable ? SETTINGS_PANEL_HEIGHT_DEV : SETTINGS_PANEL_HEIGHT_LIVE) -
  (inlineBannerEnabled ? 0 : SETTINGS_INLINE_BANNER_VERTICAL_OFFSET);

export const settingsAccountBtnCenterY = (
  screenHeight: number,
  cheatAvailable: boolean,
  inlineBannerEnabled = true,
): number =>
  screenHeight / 2 -
  settingsPanelHeight(cheatAvailable, inlineBannerEnabled) / 2 +
  SETTINGS_ACCOUNT_BTN.topOffset -
  (inlineBannerEnabled ? 0 : SETTINGS_INLINE_BANNER_VERTICAL_OFFSET);

export const VOLUME_BAR_STYLE: LevelBarStyleToken = {
  filledColor: PALETTE_NUM.sliderFill,
  emptyColor: PALETTE_NUM.sliderTrack,
  gap: 2,
  emptyAlpha: 0.9,
};

export interface SettingsModalOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly soundVolumes: Record<SoundCategory, number>;
  readonly version: string;
  readonly language: LanguageCode;
  readonly numberFormat: NumberFormat;
  readonly onChangeSoundVolume: (category: SoundCategory, level: number) => void;
  readonly onSelectLanguage: (code: LanguageCode) => void;
  readonly onSelectNumberFormat: (format: NumberFormat) => void;
  readonly targetFps: TargetFpsSetting;
  readonly onSelectTargetFps: (setting: TargetFpsSetting) => void;
  readonly onOpenAccount: () => void;
  readonly onOpenContact: () => void;
  readonly onOpenOtherGames: () => void;
  readonly accountNeedsLink?: boolean;
  readonly cheatAvailable: boolean;
  readonly onOpenCheat: () => void;
  readonly onOpenDebugDashboard: () => void;
  readonly onClose: () => void;
}
